import { useContext, useEffect, useState } from 'react'
import { ConferenceContext } from 'contexts/conference'
import { SnackbarOrigin } from '@mui/material'
import { MessageWrapper, StatusPanel } from '.'
import { VIDEO_MIRRORED, VIDEO_QUALITY } from 'zoom/config'
import { ZoomClientPartialOptions } from 'zoom/types'
import { StandardButton } from 'components/buttons'
import { CustomizedSnackBar } from 'components/snackbar/CustomizedSnackBar'
import { RollbarExceptionNames, rollbarService } from 'services/rollbar'
import { BookingContext } from 'contexts/booking'

interface State extends SnackbarOrigin {
    open: boolean
}

const ConnectionStatus: React.FC<{ isDiagnosticPage: boolean }> = ({ isDiagnosticPage }) => {
    const conference = useContext(ConferenceContext)
    const [joining, setJoining] = useState<boolean>(false)
    const [micAccessError, setMicAccessError] = useState<boolean>(false)
    const viewRetryErrorSnackbar = conference.viewRetryErrorSnackbar
    const booking = useContext(BookingContext)

    const [states] = useState<State>({
        open: false,
        vertical: 'top',
        horizontal: 'center'
    })

    const { vertical, horizontal } = states

    const logRollbarOnError = (rollbarName: RollbarExceptionNames, error?: any, optionalData?: object): void => {

        rollbarService.rollbar.error(
            rollbarName,
            {
                userId: booking.status === 'validated' ? booking.user.id : '',
                userName: booking.status === 'validated' ? booking.user.firstName : '',
                classRoomId: booking.status === 'validated' ? booking.classroomId : '',
                error,
                ...optionalData
            }
        )
    }

    const checkMediaAccess = async (constrain: MediaStreamConstraints): Promise<{ error?: string }> => {
        try {
            const mediaStream = await navigator.mediaDevices.getUserMedia(constrain)
            mediaStream.getTracks().forEach(track => track.stop())
            return {}
        } catch (error: any) {
            return { error: error?.message || error }
        }
    }

    const handleMediaAccessError = async (): Promise<boolean> => {
        const micAccessResult = await checkMediaAccess({ audio: true })
        let hasMicError = false

        if (micAccessResult?.error) {
            logRollbarOnError(RollbarExceptionNames.SystemMediaAccessError, micAccessResult.error, {
                mediaType: 'microphone'
            })
            setMicAccessError(true)
            hasMicError = true
        }
        return hasMicError
    }

    useEffect(() => {
        const diagnoseStatus = sessionStorage.getItem('diagnoseStatus')
        const { isVideoMuted, isAudioMuted, isVideoDisabled, isDiagnosed, selectedCamera } =
            JSON.parse(diagnoseStatus || '{"isVideoMuted":false,"isAudioMuted":false,"isVideoDisabled":false,"isDiagnosed":true, "selectedCamera": ""}')
        if (conference.status === 'ready-to-join' && isDiagnosed && isDiagnosticPage) {
            void (async () => {
                if (await handleMediaAccessError()) return
                try {
                    await conference.joinSession<ZoomClientPartialOptions>({
                        videoQuality: VIDEO_QUALITY,
                        video: {
                            mirrored: VIDEO_MIRRORED,
                            originalRatio: false,
                            cameraId: selectedCamera
                        },
                        isVideoMuted,
                        isAudioMuted,
                        isVideoDisabled
                    })
                } catch (error) {
                    logRollbarOnError(RollbarExceptionNames.ConnectionStatusError, error)
                    conference.setRetryError(true)
                }
            })()
        }
    }, [conference.status])

    if (conference.status === 'session-joined') {
        return null

    } else if (conference.status === 'waiting-for-classroom-info' || conference.status === 'initializing-client') {
        return (
            <StatusPanel id='session-not-joined-panel'><MessageWrapper>Checking the meeting booking</MessageWrapper></StatusPanel>
        )

    } else if (conference.status === 'ready-to-join') {
        const { selectedCamera } = JSON.parse(sessionStorage.getItem('diagnoseStatus') || '{"selectedCamera": ""}')
        if (!isDiagnosticPage && !joining) {
            return (
                <>
                    <StatusPanel id='session-not-joined-panel'>
                        <StandardButton
                            id='enter-classroom-button'
                            text='Enter the classroom'
                            color='error'
                            onClick={() => {
                                void (async () => {
                                    try {
                                        if (await handleMediaAccessError()) {
                                            setJoining(true)
                                            return
                                        }
                                        await conference.joinSession<ZoomClientPartialOptions>({
                                            videoQuality: VIDEO_QUALITY,
                                            video: {
                                                mirrored: VIDEO_MIRRORED,
                                                originalRatio: false,
                                                cameraId: selectedCamera
                                            }
                                        })
                                    } catch (error) {
                                        logRollbarOnError(RollbarExceptionNames.ReConnectionStatusError, error)
                                        conference.setRetryError(true)
                                    }
                                })()
                                setJoining(true)
                            }}
                        />
                    </StatusPanel>
                </>
            )
        } else {
            return (
                <>
                    <StatusPanel id='session-not-joined-panel'>
                        <MessageWrapper>Joining the classroom</MessageWrapper>
                    </StatusPanel>
                    <CustomizedSnackBar
                        open={viewRetryErrorSnackbar || micAccessError }
                        anchorOrigin={{ vertical, horizontal }}
                        message={
                            micAccessError
                                ? 'Your microphone is off. Please turn it on in your system or browser settings to join the class.'
                                : 'Something went wrong while joining the class. Please try again.'

                        }
                        isActionNeeded={!micAccessError && viewRetryErrorSnackbar}
                    />
                </>
            )
        }
    } else {
        return null
    }
}

export { ConnectionStatus }
